import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Bitcoin, RefreshCw, ShieldAlert, Power, PowerOff, Activity,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle,
  ArrowUpRight, ArrowDownRight, KeyRound, ChevronRight, History, Zap,
  Scale, FileDown, Info, Radar as RadarIcon, Search, Trash2, Plus, LineChart as LineChartIcon,
  Bot, Play, Save, RotateCcw, TrendingUp as TrendUp,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api, { apiError } from "../lib/api";
import { fmtEUR } from "../lib/format";
import { Money } from "../context/PrivacyContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const EXCHANGE_COLORS = { binance: "#F0B90B", coinbase: "#0052FF" };

function StatusDot({ ok }) {
  return (
    <span className={`inline-block h-2.5 w-2.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
  );
}

function KpiCard({ label, value, hint, trend, testid }) {
  const positive = trend > 0;
  const negative = trend < 0;
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-2 font-heading text-2xl font-extrabold tracking-tight tabular-nums">{value}</div>
      {hint && <div className={`mt-1 text-xs tabular-nums ${positive ? "text-emerald-600" : negative ? "text-red-600" : "text-muted-foreground"}`}>{hint}</div>}
    </div>
  );
}

function Empty({ icon: Icon = Activity, title, hint, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
      <Icon size={28} className="mx-auto text-muted-foreground" />
      <div className="mt-3 font-semibold">{title}</div>
      {hint && <div className="mt-1 text-sm text-muted-foreground">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const fmtPct = (n) => (n === null || n === undefined || isNaN(n) ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(2)}%`);
const fmtNum = (n, d = 6) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: d }).format(n ?? 0);
const fmtDateTime = (iso) => {
  if (!iso) return "Nunca";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function Cripto() {
  const { isMatriz } = useFeatureFlags();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [operations, setOperations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("resumen");
  const [syncing, setSyncing] = useState(false);
  const [opsFilter, setOpsFilter] = useState({ exchange: "all", kind: "all" });

  const load = useCallback(async () => {
    try {
      const [s, sum, pos] = await Promise.all([
        api.get("/crypto/status"),
        api.get("/crypto/summary"),
        api.get("/crypto/positions"),
      ]);
      setStatus(s.data);
      setSummary(sum.data);
      setPositions(pos.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  const loadOps = useCallback(async () => {
    const params = {};
    if (opsFilter.exchange !== "all") params.exchange = opsFilter.exchange;
    if (opsFilter.kind !== "all") params.kind = opsFilter.kind;
    const { data } = await api.get("/crypto/operations", { params });
    setOperations(data);
  }, [opsFilter]);

  const loadAudit = useCallback(async () => {
    const { data } = await api.get("/crypto/audit");
    setAudit(data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "operaciones") loadOps(); }, [tab, loadOps]);
  useEffect(() => { if (tab === "config") loadAudit(); }, [tab, loadAudit]);

  // Auto-sync al abrir + refresco automático cada 5 min mientras se está en la página
  useEffect(() => {
    // sync inicial (silencioso si no hay keys)
    if (status && (status.binance.configured || status.coinbase.configured)) {
      doSync(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.binance?.configured, status?.coinbase?.configured]);

  useEffect(() => {
    const iv = setInterval(() => { load(); }, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [load]);

  const doSync = async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/crypto/sync");
      if (!silent) {
        const parts = [];
        if (data.result.binance?.new_trades !== undefined) parts.push(`Binance: ${data.result.binance.new_trades} nuevas`);
        if (data.result.coinbase?.new_trades !== undefined) parts.push(`Coinbase: ${data.result.coinbase.new_trades} nuevas`);
        toast.success(parts.length ? `Sincronizado. ${parts.join(" · ")}` : "Sincronizado");
      }
      await load();
      if (tab === "operaciones") await loadOps();
    } catch (e) {
      if (!silent) toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const toggleRobot = async (enabled) => {
    try {
      await api.post("/crypto/robot", { enabled });
      toast.success(enabled ? "Robot activado (modo simulación V1.0)" : "Robot desactivado");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const emergencyStop = async () => {
    if (!window.confirm("¿Detener todas las automatizaciones cripto? Se registrará en auditoría.")) return;
    try {
      await api.post("/crypto/emergency-stop");
      toast.warning("Parada de emergencia activada. Robot bloqueado.");
      await load();
      await loadAudit();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetEmergency = async () => {
    try {
      await api.post("/crypto/emergency-stop/reset");
      toast.success("Parada de emergencia desactivada");
      await load();
      await loadAudit();
    } catch (e) { toast.error(apiError(e)); }
  };

  const historyChart = useMemo(() => (summary?.history || []).map((h) => ({ date: h.date, total: h.total_eur })), [summary]);

  const exchangePositions = (ex) => positions.filter((p) => p.exchange === ex);
  const totalByExchange = (ex) => exchangePositions(ex).reduce((s, p) => s + p.current_value_eur, 0);
  const pnlByExchange = (ex) => exchangePositions(ex).reduce((s, p) => s + p.unrealized_pnl_eur + p.realized_pnl_eur, 0);

  return (
    <div className="space-y-6" data-testid="cripto-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-3">
            <Bitcoin size={26} /> Activos Financieros
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cartera cripto en tiempo real — Binance & Coinbase. Datos de solo lectura.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <StatusDot ok={status?.binance?.connected} /> Binance
            </span>
            <span className="flex items-center gap-1.5">
              <StatusDot ok={status?.coinbase?.connected} /> Coinbase
            </span>
          </div>
          <Button data-testid="btn-sync-now" onClick={() => doSync(false)} disabled={syncing} variant="outline" size="sm">
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando…" : "Sincronizar"}
          </Button>
          <Button
            data-testid="btn-pdf-report"
            variant="outline"
            size="sm"
            onClick={() => {
              const y = new Date().getFullYear();
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/crypto?year=${y}`, "_blank");
            }}
          >
            <FileDown size={14} className="mr-2" /> Informe PDF
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`grid grid-cols-4 ${isMatriz ? "md:grid-cols-8" : "md:grid-cols-7"} w-full max-w-5xl`}>
          <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="binance" data-testid="tab-binance">Binance</TabsTrigger>
          <TabsTrigger value="coinbase" data-testid="tab-coinbase">Coinbase</TabsTrigger>
          <TabsTrigger value="operaciones" data-testid="tab-operaciones">Operaciones</TabsTrigger>
          <TabsTrigger value="senales" data-testid="tab-senales">Señales</TabsTrigger>
          {isMatriz && <TabsTrigger value="robot" data-testid="tab-robot">Robot</TabsTrigger>}
          <TabsTrigger value="fiscalidad" data-testid="tab-fiscalidad">Fiscalidad</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Config</TabsTrigger>
        </TabsList>

        {/* ============ RESUMEN ============ */}
        <TabsContent value="resumen" className="space-y-6 mt-6">
          {!status?.binance?.configured && !status?.coinbase?.configured ? (
            <Empty
              icon={KeyRound}
              title="Aún no hay exchanges conectados"
              hint="Configura las claves API en variables de entorno para empezar a sincronizar tu cartera."
              action={<Button onClick={() => setTab("config")}>Ver instrucciones <ChevronRight size={14} className="ml-1" /></Button>}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                  testid="kpi-value"
                  label="Patrimonio cripto"
                  value={<Money value={summary?.total_value_eur || 0} />}
                  hint={`${summary?.positions_count || 0} posiciones`}
                />
                <KpiCard
                  testid="kpi-invested"
                  label="Capital invertido"
                  value={<Money value={summary?.total_invested_eur || 0} />}
                  hint={`${summary?.operations_count || 0} operaciones`}
                />
                <KpiCard
                  testid="kpi-pnl"
                  label="P&L Total"
                  value={<Money value={summary?.pnl_total_eur || 0} />}
                  hint={`No realiz.: ${fmtEUR(summary?.unrealized_pnl_eur || 0)} · Realiz.: ${fmtEUR(summary?.realized_pnl_eur || 0)}`}
                  trend={summary?.pnl_total_eur}
                />
                <KpiCard
                  testid="kpi-roi"
                  label="ROI"
                  value={fmtPct(summary?.roi_pct || 0)}
                  hint={`Comisiones: ${fmtNum(summary?.fees_total || 0, 4)}`}
                  trend={summary?.roi_pct}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                  testid="kpi-wins"
                  label="Ganadoras"
                  value={summary?.wins || 0}
                  hint={`Media: ${fmtEUR(summary?.avg_win_eur || 0)}`}
                />
                <KpiCard
                  testid="kpi-losses"
                  label="Perdedoras"
                  value={summary?.losses || 0}
                  hint={`Media: ${fmtEUR(summary?.avg_loss_eur || 0)}`}
                />
                <KpiCard
                  testid="kpi-winrate"
                  label="Win rate"
                  value={fmtPct(summary?.win_ratio_pct || 0)}
                  hint={`Robot: ${summary?.robot_count || 0} / Manual: ${summary?.manual_count || 0}`}
                />
                <KpiCard
                  testid="kpi-fees"
                  label="Comisiones"
                  value={fmtNum(summary?.fees_total || 0, 4)}
                  hint="Aprox. (varias monedas)"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-semibold mb-3">Evolución del patrimonio cripto</h3>
                  {historyChart.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-16 text-center">Aún no hay historial. Sincroniza para empezar.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={historyChart}>
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtEUR(v)} width={80} />
                        <Tooltip formatter={(v) => fmtEUR(v)} />
                        <Line type="monotone" dataKey="total" stroke="#F0B90B" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-semibold mb-3">Distribución por exchange</h3>
                  {(summary?.by_exchange || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground py-16 text-center">Sin datos.</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={summary.by_exchange}
                            dataKey="total_eur"
                            nameKey="exchange"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                          >
                            {summary.by_exchange.map((e) => (
                              <Cell key={e.exchange} fill={EXCHANGE_COLORS[e.exchange] || "#94A3B8"} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => fmtEUR(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-1 mt-2">
                        {summary.by_exchange.map((e) => (
                          <div key={e.exchange} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 capitalize">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ background: EXCHANGE_COLORS[e.exchange] }} />
                              {e.exchange}
                            </span>
                            <span className="tabular-nums">{fmtEUR(e.total_eur)} · {e.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-semibold mb-3">Top posiciones</h3>
                {positions.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Aún no hay posiciones. Sincroniza para verlas.</div>
                ) : (
                  <PositionsTable positions={positions.slice(0, 10)} showExchange />
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* ============ BINANCE ============ */}
        <TabsContent value="binance" className="space-y-6 mt-6">
          <ExchangeView
            name="Binance"
            connected={status?.binance?.connected}
            configured={status?.binance?.configured}
            lastSync={status?.binance?.last_sync}
            lastError={status?.binance?.last_error}
            positions={exchangePositions("binance")}
            total={totalByExchange("binance")}
            pnl={pnlByExchange("binance")}
            color="#F0B90B"
            onGoConfig={() => setTab("config")}
          />
        </TabsContent>

        {/* ============ COINBASE ============ */}
        <TabsContent value="coinbase" className="space-y-6 mt-6">
          <ExchangeView
            name="Coinbase"
            connected={status?.coinbase?.connected}
            configured={status?.coinbase?.configured}
            lastSync={status?.coinbase?.last_sync}
            lastError={status?.coinbase?.last_error}
            positions={exchangePositions("coinbase")}
            total={totalByExchange("coinbase")}
            pnl={pnlByExchange("coinbase")}
            color="#0052FF"
            onGoConfig={() => setTab("config")}
          />
        </TabsContent>

        {/* ============ OPERACIONES ============ */}
        <TabsContent value="operaciones" className="space-y-4 mt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={opsFilter.exchange} onValueChange={(v) => setOpsFilter({ ...opsFilter, exchange: v })}>
              <SelectTrigger className="w-40" data-testid="ops-filter-exchange"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los exchanges</SelectItem>
                <SelectItem value="binance">Binance</SelectItem>
                <SelectItem value="coinbase">Coinbase</SelectItem>
              </SelectContent>
            </Select>
            <Select value={opsFilter.kind} onValueChange={(v) => setOpsFilter({ ...opsFilter, kind: v })}>
              <SelectTrigger className="w-40" data-testid="ops-filter-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Manual + Robot</SelectItem>
                <SelectItem value="manual">Solo manuales</SelectItem>
                <SelectItem value="robot">Solo robot</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground ml-auto">{operations.length} operaciones</div>
          </div>
          {operations.length === 0 ? (
            <Empty icon={History} title="Sin operaciones" hint="Cuando haya trades sincronizados aparecerán aquí clasificados por Manual o Robot." />
          ) : (
            <OperationsTable ops={operations} />
          )}
        </TabsContent>

        {/* ============ SEÑALES ============ */}
        <TabsContent value="senales" className="space-y-6 mt-6">
          <SenalesPanel />
        </TabsContent>

        {/* ============ ROBOT V1.3 (MATRIZ ONLY) ============ */}
        {isMatriz && (
          <TabsContent value="robot" className="space-y-6 mt-6">
            <RobotPanel />
          </TabsContent>
        )}

        {/* ============ FISCALIDAD ============ */}
        <TabsContent value="fiscalidad" className="space-y-6 mt-6">
          <FiscalidadPanel />
        </TabsContent>

        {/* ============ CONFIG ============ */}
        <TabsContent value="config" className="space-y-6 mt-6">
          <ConfigPanel
            status={status}
            audit={audit}
            onToggleRobot={toggleRobot}
            onEmergencyStop={emergencyStop}
            onEmergencyReset={resetEmergency}
            syncing={syncing}
            onSync={() => doSync(false)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Vista por exchange ----------
function ExchangeView({ name, connected, configured, lastSync, lastError, positions, total, pnl, color, onGoConfig }) {
  if (!configured) {
    return (
      <Empty
        icon={KeyRound}
        title={`${name} no está configurado`}
        hint={`Añade las variables de entorno correspondientes para conectar tu cuenta.`}
        action={<Button onClick={onGoConfig}>Ver configuración</Button>}
      />
    );
  }
  return (
    <>
      <div className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full flex items-center justify-center font-heading font-black text-black" style={{ background: color }}>
              {name[0]}
            </span>
            <div>
              <div className="font-heading text-lg font-extrabold flex items-center gap-2">
                {name} <StatusDot ok={connected} />
              </div>
              <div className="text-xs text-muted-foreground">Última sincronización: {fmtDateTime(lastSync)}</div>
            </div>
          </div>
          {lastError && (
            <div className="mt-2 text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={12} /> {lastError}</div>
          )}
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Valor</div>
            <div className="font-heading font-extrabold text-xl tabular-nums"><Money value={total} /></div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">P&L</div>
            <div className={`font-heading font-extrabold text-xl tabular-nums ${pnl > 0 ? "text-emerald-600" : pnl < 0 ? "text-red-600" : ""}`}>
              <Money value={pnl} />
            </div>
          </div>
        </div>
      </div>
      {positions.length === 0 ? (
        <Empty icon={Activity} title={`Sin posiciones en ${name}`} hint="Cuando haya balances y trades sincronizados aparecerán aquí." />
      ) : (
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-semibold mb-3">Posiciones abiertas</h3>
          <PositionsTable positions={positions} />
        </div>
      )}
    </>
  );
}

// ---------- Tabla de posiciones ----------
function PositionsTable({ positions, showExchange = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground tracking-widest">
          <tr className="border-b border-border">
            <th className="text-left py-2">Activo</th>
            {showExchange && <th className="text-left py-2">Exchange</th>}
            <th className="text-right py-2">Cantidad</th>
            <th className="text-right py-2">Precio medio</th>
            <th className="text-right py-2">Precio actual</th>
            <th className="text-right py-2">Valor</th>
            <th className="text-right py-2">P&L</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {positions.map((p) => {
            const pnl = p.unrealized_pnl_eur + p.realized_pnl_eur;
            const pos = pnl > 0;
            return (
              <tr key={`${p.exchange}-${p.asset}`} className="border-b border-border last:border-0" data-testid={`pos-${p.exchange}-${p.asset}`}>
                <td className="py-2 font-semibold">{p.asset}</td>
                {showExchange && <td className="py-2 capitalize"><Badge variant="outline">{p.exchange}</Badge></td>}
                <td className="py-2 text-right">{fmtNum(p.qty, 8)}</td>
                <td className="py-2 text-right">{fmtEUR(p.avg_price_eur)}</td>
                <td className="py-2 text-right">{fmtEUR(p.current_price_eur)}</td>
                <td className="py-2 text-right font-semibold"><Money value={p.current_value_eur} /></td>
                <td className={`py-2 text-right font-semibold ${pos ? "text-emerald-600" : pnl < 0 ? "text-red-600" : ""}`}>
                  {pos ? <ArrowUpRight size={12} className="inline" /> : pnl < 0 ? <ArrowDownRight size={12} className="inline" /> : null}
                  <Money value={pnl} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tabla de operaciones ----------
function OperationsTable({ ops }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground tracking-widest">
          <tr className="border-b border-border bg-secondary/40">
            <th className="text-left py-3 px-3">Fecha</th>
            <th className="text-left py-3 px-3">Exchange</th>
            <th className="text-left py-3 px-3">Par</th>
            <th className="text-left py-3 px-3">Lado</th>
            <th className="text-right py-3 px-3">Cantidad</th>
            <th className="text-right py-3 px-3">Precio</th>
            <th className="text-right py-3 px-3">Total</th>
            <th className="text-right py-3 px-3">Fee</th>
            <th className="text-left py-3 px-3">Origen</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {ops.map((o) => (
            <tr key={`${o.exchange}-${o.trade_id}`} className="border-b border-border last:border-0">
              <td className="py-2 px-3">{fmtDateTime(new Date(o.timestamp_ms).toISOString())}</td>
              <td className="py-2 px-3 capitalize">{o.exchange}</td>
              <td className="py-2 px-3 font-semibold">{o.base}/{o.quote}</td>
              <td className={`py-2 px-3 font-semibold ${o.side === "BUY" ? "text-emerald-600" : "text-red-600"}`}>{o.side}</td>
              <td className="py-2 px-3 text-right">{fmtNum(o.qty, 8)}</td>
              <td className="py-2 px-3 text-right">{fmtNum(o.price, 4)}</td>
              <td className="py-2 px-3 text-right">{fmtNum(o.quote_qty, 2)} {o.quote}</td>
              <td className="py-2 px-3 text-right">{fmtNum(o.fee, 6)} {o.fee_asset}</td>
              <td className="py-2 px-3">
                <Badge variant={o.classification === "robot" ? "default" : "outline"}>
                  {o.classification === "robot" ? "Robot" : "Manual"}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Panel de configuración ----------
function ConfigPanel({ status, audit, onToggleRobot, onEmergencyStop, onEmergencyReset, onSync, syncing }) {
  const robotEnabled = status?.robot?.enabled;
  const emergency = status?.robot?.emergency_stop;

  return (
    <>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><KeyRound size={16} /> Estado de conexión</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <ExchangeStatus name="Binance" info={status?.binance} envVars={["BINANCE_API_KEY", "BINANCE_API_SECRET"]} />
          <ExchangeStatus name="Coinbase" info={status?.coinbase} envVars={["COINBASE_API_KEY", "COINBASE_API_SECRET"]} />
        </div>
        <div className="mt-5 flex items-center gap-2">
          <Button onClick={onSync} disabled={syncing} data-testid="btn-sync-config">
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            Sincronizar ahora
          </Button>
          <p className="text-xs text-muted-foreground">
            Sincronización automática cada hora en segundo plano + al abrir el módulo.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Zap size={16} /> Automatización (V1.3 preview)</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-xl border border-border">
            <div>
              <div className="font-medium flex items-center gap-2">
                {robotEnabled ? <Power size={14} className="text-emerald-600" /> : <PowerOff size={14} className="text-muted-foreground" />}
                Robot Trading
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                UI preparada para V1.3. Actualmente <b>no ejecuta órdenes reales</b>.
              </div>
            </div>
            <Switch
              data-testid="switch-robot"
              checked={!!robotEnabled}
              disabled={emergency}
              onCheckedChange={onToggleRobot}
            />
          </div>

          <div className={`p-3 rounded-xl border ${emergency ? "border-red-500 bg-red-50 dark:bg-red-950/20" : "border-border"}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <ShieldAlert size={14} className={emergency ? "text-red-600" : ""} />
                  Parada de emergencia
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Bloquea inmediatamente cualquier automatización y estrategia. Registro en auditoría.
                </div>
                {emergency && (
                  <div className="text-xs text-red-600 mt-1 font-semibold">
                    ACTIVA — desde {fmtDateTime(status?.robot?.last_emergency_change)}
                  </div>
                )}
              </div>
              {emergency ? (
                <Button variant="outline" onClick={onEmergencyReset} data-testid="btn-emergency-reset">
                  <CheckCircle2 size={14} className="mr-2" /> Desactivar
                </Button>
              ) : (
                <Button variant="destructive" onClick={onEmergencyStop} data-testid="btn-emergency-stop">
                  <ShieldAlert size={14} className="mr-2" /> Detener automatizaciones
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><History size={16} /> Auditoría</h3>
        {audit.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Sin eventos registrados.</div>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-border">
            {audit.map((a, i) => (
              <div key={i} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-mono text-[11px] text-muted-foreground">{fmtDateTime(a.created_at)}</div>
                  <div>{humanAction(a.action)}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.detail && Object.entries(a.detail).map(([k, v]) => (
                    <span key={k} className="ml-2">{k}: <span className="font-mono">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-secondary/40 p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle size={16} /> Seguridad</h3>
        <ul className="text-sm space-y-1.5 text-muted-foreground list-disc pl-5">
          <li>Las API keys se leen exclusivamente de variables de entorno del servidor.</li>
          <li>Nunca se solicitan ni almacenan claves desde la interfaz o la base de datos.</li>
          <li>Se recomienda crear las claves con permisos <b>solo lectura</b>.</li>
          <li>En Binance, restringe la IP y desactiva retiros. En Coinbase, usa API keys con scope <code>view</code>.</li>
        </ul>
      </div>
    </>
  );
}

function ExchangeStatus({ name, info, envVars }) {
  if (!info) return null;
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="font-heading font-extrabold">{name}</div>
        {info.configured ? (
          info.connected ? (
            <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 border-emerald-500/30">
              <CheckCircle2 size={12} className="mr-1" /> Conectado
            </Badge>
          ) : (
            <Badge variant="destructive">
              <XCircle size={12} className="mr-1" /> Error
            </Badge>
          )
        ) : (
          <Badge variant="outline">
            <XCircle size={12} className="mr-1" /> Desconectado
          </Badge>
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-2">
        Última sincronización: <span className="text-foreground font-mono">{fmtDateTime(info.last_sync)}</span>
      </div>
      {info.last_error && (
        <div className="text-xs text-red-600 mt-2">
          <AlertTriangle size={11} className="inline mr-1" /> {info.last_error}
        </div>
      )}
      {!info.configured && (
        <div className="text-xs mt-2 bg-secondary/40 rounded p-2">
          <div className="font-semibold">Variables requeridas:</div>
          <ul className="font-mono mt-1 space-y-0.5">
            {envVars.map((v) => <li key={v}>{v}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function humanAction(a) {
  const map = {
    sync_binance: "Sincronización Binance",
    sync_coinbase: "Sincronización Coinbase",
    sync_binance_error: "Error sincronizando Binance",
    sync_coinbase_error: "Error sincronizando Coinbase",
    robot_toggle: "Cambio robot",
    emergency_stop: "Parada de emergencia activada",
    emergency_reset: "Parada de emergencia desactivada",
  };
  return map[a] || a;
}

// ============================================================
//  FISCALIDAD (V1.1 FIFO España)
// ============================================================
function FiscalidadPanel() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => {
    api.get("/crypto/tax/years").then((r) => {
      const ys = r.data.years || [];
      setYears(ys);
      if (ys.length) setYear(String(ys[ys.length - 1]));
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    if (!year) {
      // sin años (todavía sin operaciones): pedir el informe global para conocer Modelo 721 y lotes
      setLoading(true);
      api.get("/crypto/tax/report").then((r) => setReport(r.data)).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    api.get(`/crypto/tax/report?year=${year}`)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [year]);

  const download = async () => {
    if (!year) return;
    try {
      const res = await api.get(`/crypto/tax/export?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fiscalidad_cripto_${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error(apiError(e)); }
  };

  const detail = report?.year_detail;
  const summary = detail?.summary;
  const events = detail?.events || [];
  const openLots = report?.open_lots || [];
  const modelo721 = report?.modelo_721;

  return (
    <div className="space-y-6" data-testid="fiscalidad-panel">
      {/* Alerta Modelo 721 */}
      {modelo721?.applies && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="modelo-721-warning">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <div>
            <div className="font-semibold text-amber-900 dark:text-amber-300">Posible obligación informativa Modelo 721</div>
            <div className="text-sm text-amber-800/90 dark:text-amber-200/80 mt-1">
              Tienes {fmtEUR(modelo721.current_non_resident_eur)} en exchanges no residentes ({modelo721.exchanges_included.join(", ")}), superando el umbral de {fmtEUR(modelo721.threshold_eur)}. Revisa tu situación fiscal con tu asesor.
            </div>
          </div>
        </div>
      )}

      {/* Selector año + export */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-muted-foreground">Año fiscal:</label>
        <Select value={year} onValueChange={setYear} disabled={!years.length}>
          <SelectTrigger className="w-32" data-testid="fiscal-year-select"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={download} disabled={!year || !events.length} data-testid="btn-fiscal-export">
          <FileDown size={14} className="mr-2" /> Exportar CSV
        </Button>
        <div className="text-xs text-muted-foreground ml-auto flex items-center gap-1.5">
          <Scale size={12} /> Método FIFO · Base imponible del ahorro (IRPF)
        </div>
      </div>

      {!year && years.length === 0 && (
        <Empty
          icon={Scale}
          title="Sin operaciones fiscales todavía"
          hint="Cuando haya ventas o permutas sincronizadas, aparecerá aquí el detalle FIFO con la ganancia/pérdida, el IRPF estimado y las pérdidas a compensar en años posteriores."
        />
      )}

      {loading && <div className="text-sm text-muted-foreground">Calculando FIFO…</div>}

      {summary && (
        <>
          {/* KPIs fiscales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testid="fiscal-kpi-gains" label="Ganancias" value={fmtEUR(summary.total_gain)} hint={`${summary.events_count} hechos imponibles`} trend={summary.total_gain} />
            <KpiCard testid="fiscal-kpi-losses" label="Pérdidas" value={fmtEUR(summary.total_loss)} hint="Del año en curso" trend={-summary.total_loss} />
            <KpiCard testid="fiscal-kpi-base" label="Base imponible ahorro" value={fmtEUR(summary.taxable_base_eur)} hint={`Bruto: ${fmtEUR(summary.gross_result)} · Compensadas: ${fmtEUR(summary.losses_compensated_eur)}`} />
            <KpiCard testid="fiscal-kpi-irpf" label="IRPF estimado" value={fmtEUR(summary.irpf_estimated_eur)} hint={`Tramos 19/21/23/27/28%`} />
          </div>

          {/* Pérdidas a compensar */}
          {summary.losses_carried_eur > 0 && (
            <div className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3" data-testid="losses-carried">
              <Info size={16} className="mt-0.5 text-blue-600" />
              <div className="text-sm">
                <div className="font-semibold">Pérdidas pendientes de compensar: {fmtEUR(summary.losses_carried_eur)}</div>
                <div className="text-muted-foreground mt-1">Compensables en años posteriores (máximo 4 años desde la pérdida original).</div>
                <ul className="mt-2 space-y-0.5">
                  {(summary.losses_carried_detail || []).map((d, i) => (
                    <li key={i} className="text-xs">
                      Origen {d.year}: <b>{fmtEUR(d.remaining_eur)}</b> · vigencia hasta {d.expires_after}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Tabla de hechos imponibles */}
          {events.length === 0 ? (
            <Empty icon={Scale} title={`Sin hechos imponibles en ${year}`} hint="En este año no se han detectado ventas ni permutas cripto." />
          ) : (
            <div className="rounded-2xl border border-border bg-card">
              <div className="p-5 pb-3 font-semibold flex items-center gap-2">
                Hechos imponibles ({events.length})
                <Badge variant="outline" className="ml-2 text-[10px]">FIFO por (exchange, activo)</Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground tracking-widest">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-4">Fecha</th>
                      <th className="text-left py-2 px-4">Activo</th>
                      <th className="text-left py-2 px-4">Exchange</th>
                      <th className="text-left py-2 px-4">Tipo</th>
                      <th className="text-right py-2 px-4">Cantidad</th>
                      <th className="text-right py-2 px-4">Ingresos €</th>
                      <th className="text-right py-2 px-4">Coste base €</th>
                      <th className="text-right py-2 px-4">Resultado €</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {events.map((e, i) => {
                      const pos = e.gain_eur > 0;
                      const expanded = expandedEvent === i;
                      return (
                        <>
                          <tr
                            key={i}
                            className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40"
                            onClick={() => setExpandedEvent(expanded ? null : i)}
                            data-testid={`fiscal-event-${i}`}
                          >
                            <td className="py-2 px-4">{e.sell_date}</td>
                            <td className="py-2 px-4 font-semibold">{e.asset}</td>
                            <td className="py-2 px-4 capitalize"><Badge variant="outline" className="text-[10px]">{e.exchange}</Badge></td>
                            <td className="py-2 px-4">
                              <Badge variant={e.kind === "permuta" ? "default" : "outline"} className="text-[10px]">
                                {e.kind === "permuta" ? `Permuta → ${e.counter_asset}` : "Venta a EUR"}
                              </Badge>
                            </td>
                            <td className="py-2 px-4 text-right">{fmtNum(e.qty, 8)}</td>
                            <td className="py-2 px-4 text-right">{fmtEUR(e.proceeds_eur)}</td>
                            <td className="py-2 px-4 text-right text-muted-foreground">{fmtEUR(e.cost_basis_eur)}</td>
                            <td className={`py-2 px-4 text-right font-semibold ${pos ? "text-emerald-600" : e.gain_eur < 0 ? "text-red-600" : ""}`}>
                              {fmtEUR(e.gain_eur)}
                            </td>
                            <td className="py-2 pr-3 text-muted-foreground">
                              <ChevronRight size={14} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="bg-secondary/20 border-b border-border">
                              <td colSpan={9} className="p-4">
                                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Lotes consumidos (FIFO)</div>
                                <table className="w-full text-xs tabular-nums">
                                  <thead className="text-[10px] uppercase text-muted-foreground">
                                    <tr>
                                      <th className="text-left py-1">Fecha compra</th>
                                      <th className="text-right py-1">Cantidad</th>
                                      <th className="text-right py-1">Coste unitario €</th>
                                      <th className="text-right py-1">Coste total €</th>
                                      <th className="text-left py-1 pl-3">Nota</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {e.lots_consumed.map((l, j) => (
                                      <tr key={j} className="border-t border-border">
                                        <td className="py-1">{l.buy_date || "—"}</td>
                                        <td className="py-1 text-right">{fmtNum(l.qty_consumed, 8)}</td>
                                        <td className="py-1 text-right">{fmtEUR(l.cost_per_unit_eur)}</td>
                                        <td className="py-1 text-right">{fmtEUR(l.cost_eur)}</td>
                                        <td className="py-1 pl-3 text-muted-foreground">{l.note === "sin_lote_previo" ? "Sin compra previa registrada (coste 0)" : ""}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Lotes abiertos */}
          <div className="rounded-2xl border border-border bg-card">
            <div className="p-5 pb-3 font-semibold flex items-center gap-2">
              Lotes abiertos ({openLots.length})
              <Badge variant="outline" className="ml-2 text-[10px]">Coste base fiscal actual</Badge>
            </div>
            {openLots.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Sin lotes abiertos.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground tracking-widest">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-4">Activo</th>
                      <th className="text-left py-2 px-4">Exchange</th>
                      <th className="text-left py-2 px-4">Fecha compra</th>
                      <th className="text-right py-2 px-4">Cantidad</th>
                      <th className="text-right py-2 px-4">Coste unit. €</th>
                      <th className="text-right py-2 px-4">Coste total €</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {openLots.map((l, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="py-2 px-4 font-semibold">{l.asset}</td>
                        <td className="py-2 px-4 capitalize"><Badge variant="outline" className="text-[10px]">{l.exchange}</Badge></td>
                        <td className="py-2 px-4">{l.buy_date}</td>
                        <td className="py-2 px-4 text-right">{fmtNum(l.qty, 8)}</td>
                        <td className="py-2 px-4 text-right">{fmtEUR(l.cost_per_unit_eur)}</td>
                        <td className="py-2 px-4 text-right font-semibold">{fmtEUR(l.cost_total_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Nota legal */}
          <div className="rounded-2xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Cálculo orientativo.</b> Se aplica método FIFO por (exchange, activo). Las permutas cripto→cripto se tratan como hecho imponible (art. 37.1.h LIRPF). Las pérdidas se compensan automáticamente con ganancias de los 4 años siguientes. La cuota IRPF mostrada aplica los tramos vigentes del ahorro (19/21/23/27/28%) sin considerar rendimientos del capital mobiliario, mínimos personales, ni compensaciones cruzadas. <b>Consulta a tu asesor fiscal antes de declarar.</b>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================
//  SEÑALES TÉCNICAS (V1.2)
// ============================================================
const INTERVALS = [
  { value: "15m", label: "15m" },
  { value: "1h", label: "1h" },
  { value: "4h", label: "4h" },
  { value: "1d", label: "1d" },
  { value: "1w", label: "1w" },
];

const VERDICT_COLORS = {
  STRONG_BUY: { bg: "bg-emerald-500/15", text: "text-emerald-700", border: "border-emerald-500/40", label: "Compra fuerte" },
  BUY: { bg: "bg-emerald-500/10", text: "text-emerald-700", border: "border-emerald-500/30", label: "Compra" },
  NEUTRAL: { bg: "bg-secondary", text: "text-muted-foreground", border: "border-border", label: "Neutro" },
  SELL: { bg: "bg-red-500/10", text: "text-red-700", border: "border-red-500/30", label: "Venta" },
  STRONG_SELL: { bg: "bg-red-500/15", text: "text-red-700", border: "border-red-500/40", label: "Venta fuerte" },
};

function VerdictBadge({ verdict, score }) {
  const c = VERDICT_COLORS[verdict] || VERDICT_COLORS.NEUTRAL;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${c.bg} ${c.text} ${c.border}`}>
      {c.label}
      {score !== undefined && score !== null && <span className="opacity-70">{score > 0 ? "+" : ""}{score}</span>}
    </span>
  );
}

function SignalBar({ score }) {
  // Score -100 a +100 → barra visual centrada
  const pct = Math.max(-100, Math.min(100, score ?? 0));
  const width = Math.abs(pct);
  const positive = pct >= 0;
  return (
    <div className="relative h-2 w-full bg-secondary rounded-full overflow-hidden">
      <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border" />
      <div
        className={`absolute top-0 bottom-0 ${positive ? "bg-emerald-500 left-1/2" : "bg-red-500 right-1/2"}`}
        style={{ width: `${width / 2}%` }}
      />
    </div>
  );
}

function SenalesPanel() {
  const [watchlist, setWatchlist] = useState([]);
  const [selected, setSelected] = useState(null);
  const [interval, setInterval] = useState("1h");
  const [analysis, setAnalysis] = useState(null);
  const [multi, setMulti] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newSymbol, setNewSymbol] = useState("");
  const [wlLoading, setWlLoading] = useState(true);

  const loadWatchlist = useCallback(async () => {
    setWlLoading(true);
    try {
      const { data } = await api.get("/crypto/signals/watchlist");
      setWatchlist(data.items || []);
      if (!selected && data.items?.length) setSelected(data.items[0].symbol);
    } catch (e) { toast.error(apiError(e)); }
    finally { setWlLoading(false); }
  }, [selected]);

  useEffect(() => { loadWatchlist(); }, []);  // eslint-disable-line

  const loadAnalysis = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    setAnalysis(null); setMulti(null);
    try {
      const [a, m] = await Promise.all([
        api.get(`/crypto/signals/analyze?symbol=${selected}&interval=${interval}`),
        api.get(`/crypto/signals/multi?symbol=${selected}`),
      ]);
      setAnalysis(a.data);
      setMulti(m.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [selected, interval]);

  useEffect(() => { loadAnalysis(); }, [loadAnalysis]);

  const addSymbol = async () => {
    const s = newSymbol.trim().toUpperCase();
    if (!s) return;
    try {
      await api.post("/crypto/signals/watchlist", { symbol: s, interval });
      toast.success(`${s} añadido a la watchlist`);
      setNewSymbol("");
      await loadWatchlist();
      setSelected(s);
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeSymbol = async (s) => {
    try {
      await api.delete(`/crypto/signals/watchlist/${s}`);
      toast.success(`${s} eliminado`);
      await loadWatchlist();
      if (selected === s) setSelected(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="senales-panel">
      {/* Watchlist */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><RadarIcon size={16} /> Watchlist</h3>
            <div className="text-xs text-muted-foreground mt-0.5">
              Símbolos monitorizados con score técnico en tiempo real (timeframe 1h).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              data-testid="watchlist-add-input"
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && addSymbol()}
              placeholder="Ej: SOLEUR"
              className="w-32 h-9"
            />
            <Button size="sm" onClick={addSymbol} disabled={!newSymbol.trim()} data-testid="watchlist-add-btn">
              <Plus size={14} className="mr-1" /> Añadir
            </Button>
            <Button size="sm" variant="outline" onClick={loadWatchlist} disabled={wlLoading}>
              <RefreshCw size={14} className={wlLoading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>

        {wlLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Cargando watchlist…</div>
        ) : watchlist.length === 0 ? (
          <Empty icon={RadarIcon} title="Watchlist vacía" hint="Añade símbolos como BTCEUR, ETHEUR, SOLUSDT..." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {watchlist.map((w) => {
              const active = selected === w.symbol;
              return (
                <div
                  key={w.symbol}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(w.symbol)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelected(w.symbol); }}
                  className={`text-left p-3 rounded-xl border transition-all cursor-pointer ${active ? "border-foreground bg-secondary/60" : "border-border hover:bg-secondary/30"}`}
                  data-testid={`watch-item-${w.symbol}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-extrabold text-sm">{w.symbol}</div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeSymbol(w.symbol); }}
                      className="opacity-40 hover:opacity-100 text-muted-foreground"
                      data-testid={`watch-remove-${w.symbol}`}
                      aria-label={`Eliminar ${w.symbol}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  {w.error ? (
                    <div className="text-[10px] text-red-600 mt-1 truncate">{w.error}</div>
                  ) : (
                    <>
                      <div className="text-lg font-bold tabular-nums mt-1">{fmtNum(w.price, 4)}</div>
                      <div className="mt-1"><VerdictBadge verdict={w.verdict} score={w.score} /></div>
                      <div className="mt-2"><SignalBar score={w.score} /></div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Análisis detallado */}
      {selected && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-semibold text-lg">Análisis técnico · {selected}</h3>
            <div className="flex gap-1">
              {INTERVALS.map((iv) => (
                <button
                  key={iv.value}
                  onClick={() => setInterval(iv.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${interval === iv.value ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}
                  data-testid={`tf-${iv.value}`}
                >
                  {iv.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={loadAnalysis} disabled={loading}>
              <RefreshCw size={12} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
            </Button>
          </div>

          {loading && <div className="text-sm text-muted-foreground">Analizando velas…</div>}
          {analysis?.error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-700">
              <AlertTriangle size={14} className="inline mr-1" /> {analysis.error}
            </div>
          )}

          {analysis && !analysis.error && (
            <>
              {/* KPI resumen */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard testid="senales-kpi-precio" label="Precio" value={fmtNum(analysis.price, 4)} hint={`${analysis.candles_count} velas`} />
                <KpiCard testid="senales-kpi-score" label="Score técnico" value={`${analysis.score > 0 ? "+" : ""}${analysis.score}`} hint={interval} trend={analysis.score} />
                <div className="rounded-2xl border border-border bg-card p-5 flex flex-col justify-center" data-testid="senales-kpi-verdict">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Veredicto</div>
                  <div className="mt-2"><VerdictBadge verdict={analysis.verdict} score={analysis.score} /></div>
                  <div className="mt-2"><SignalBar score={analysis.score} /></div>
                </div>
                <KpiCard testid="senales-kpi-composite" label="Score compuesto MTF" value={multi ? `${multi.composite_score > 0 ? "+" : ""}${multi.composite_score}` : "…"} hint="Ponderado 1h/4h/1d/1w" trend={multi?.composite_score} />
              </div>

              {/* Multi-timeframe */}
              {multi && (
                <div className="rounded-2xl border border-border bg-card p-5">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><LineChartIcon size={14} /> Multi-timeframe</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(multi.timeframes).map(([tf, r]) => (
                      <div key={tf} className="rounded-xl border border-border p-3" data-testid={`mtf-${tf}`}>
                        <div className="text-xs font-mono text-muted-foreground uppercase">{tf}</div>
                        {r.error ? (
                          <div className="text-xs text-red-600 mt-1">Error</div>
                        ) : (
                          <>
                            <div className="text-lg font-bold tabular-nums">{r.score > 0 ? "+" : ""}{r.score}</div>
                            <div className="mt-1"><VerdictBadge verdict={r.verdict} /></div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detalle de señales */}
              <div className="rounded-2xl border border-border bg-card">
                <div className="p-5 pb-3 font-semibold">Desglose de indicadores</div>
                <div className="divide-y divide-border">
                  {analysis.signals.map((s, i) => {
                    const barColor = s.signal > 0 ? "bg-emerald-500" : s.signal < 0 ? "bg-red-500" : "bg-muted-foreground";
                    return (
                      <div key={i} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`signal-${s.name}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold text-sm">{s.name}</div>
                            {s.value !== null && s.value !== undefined && (
                              <span className="text-xs text-muted-foreground font-mono">{typeof s.value === "number" ? s.value.toFixed(2) : s.value}</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{s.reason}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-0.5">
                            {[-2, -1, 0, 1, 2].map((n) => (
                              <div
                                key={n}
                                className={`h-6 w-2 rounded-sm ${
                                  (s.signal > 0 && n > 0 && n <= s.signal) ||
                                  (s.signal < 0 && n < 0 && n >= s.signal) ||
                                  (s.signal === 0 && n === 0)
                                    ? barColor
                                    : "bg-secondary"
                                }`}
                              />
                            ))}
                          </div>
                          <span className={`font-mono text-xs w-8 text-right ${s.signal > 0 ? "text-emerald-600" : s.signal < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                            {s.signal > 0 ? "+" : ""}{s.signal}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Niveles Fibonacci */}
              {analysis.fibonacci?.levels && Object.keys(analysis.fibonacci.levels).length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-5">
                  <h4 className="font-semibold mb-3">Niveles Fibonacci</h4>
                  <div className="text-xs text-muted-foreground mb-3">
                    Swing: {fmtNum(analysis.fibonacci.swing_low, 4)} — {fmtNum(analysis.fibonacci.swing_high, 4)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 tabular-nums">
                    {Object.entries(analysis.fibonacci.levels).map(([lvl, price]) => (
                      <div key={lvl} className="p-2 rounded-lg border border-border text-sm flex items-center justify-between">
                        <span className="font-mono text-xs text-muted-foreground">{lvl}%</span>
                        <span className="font-semibold">{fmtNum(price, 4)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mini chart */}
              {analysis.chart?.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-5">
                  <h4 className="font-semibold mb-3">Últimas velas (cierre)</h4>
                  <div style={{ width: "100%", height: 220, minHeight: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analysis.chart.map((c) => ({ time: new Date(c.t).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit" }), close: c.c }))}>
                        <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} domain={["dataMin", "dataMax"]} width={70} />
                        <Tooltip />
                        <Line type="monotone" dataKey="close" stroke="#3B82F6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground flex items-start gap-2">
                <Info size={14} className="mt-0.5 shrink-0" />
                <div>
                  <b>Motor V1.2.</b> Score agregado ponderado (RSI, MACD, Estocástico, Fibonacci, Patrones de velas). Este análisis es orientativo y <b>no constituye recomendación de inversión</b>. En V1.3 el robot podrá ejecutar órdenes basadas en estas señales.
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
//  ROBOT TRADING V1.3
// ============================================================
const ROBOT_TF_OPTIONS = ["1h", "4h", "1d"];
const ROBOT_DAYS_OPTIONS = [30, 90, 180, 365];

function RobotPanel() {
  const [config, setConfig] = useState(null);
  const [state, setState] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [btSymbol, setBtSymbol] = useState("BTCEUR");
  const [btInterval, setBtInterval] = useState("4h");
  const [btDays, setBtDays] = useState(90);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [subtab, setSubtab] = useState("backtest");

  const loadAll = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/crypto/robot/config"), api.get("/crypto/robot/state")]);
      setConfig(c.data.config);
      setState(s.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateCfg = (key, value) => {
    setConfig({ ...config, [key]: value });
    setDirty(true);
  };

  const saveCfg = async () => {
    setSaving(true);
    try {
      const patch = { ...config };
      delete patch.family_id; delete patch.updated_at; delete patch.symbols_prepared;
      const { data } = await api.put("/crypto/robot/config", patch);
      setConfig(data.config);
      setDirty(false);
      toast.success("Configuración guardada");
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const runBacktest = async () => {
    setRunning(true);
    setBacktest(null);
    try {
      const { data } = await api.post("/crypto/robot/backtest", { symbol: btSymbol, interval: btInterval, days: btDays });
      if (data.error) { toast.error(data.error); return; }
      setBacktest(data);
      toast.success(`Backtest completado: ${data.metrics.trades_count} trades, ROI ${data.metrics.roi_pct}%`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };

  const runPaperStep = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/crypto/robot/paper/step");
      if (data.skipped) {
        toast.warning(`Paso saltado: ${data.skipped}`);
      } else {
        const events = data.events || [];
        const active = events.filter((e) => e.action && e.action !== "HOLD").length;
        toast.success(`Iteración ejecutada: ${events.length} evaluaciones · ${active} acciones`);
      }
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };

  const resetPaper = async () => {
    if (!window.confirm("¿Resetear el portfolio virtual? Se borrarán posiciones y operaciones simuladas.")) return;
    try {
      await api.post("/crypto/robot/paper/reset");
      toast.success("Portfolio virtual reseteado");
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!config || !state) return <div className="text-sm text-muted-foreground">Cargando robot…</div>;

  return (
    <div className="space-y-6" data-testid="robot-panel">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Bot size={16} /> Estado del Robot</h3>
            <div className="text-xs text-muted-foreground mt-0.5">
              Modo Paper Trading. Trading real bloqueado hasta V1.4.
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={state.robot_enabled ? "default" : "outline"} className={state.robot_enabled ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" : ""}>
              {state.robot_enabled ? <Power size={12} className="mr-1" /> : <PowerOff size={12} className="mr-1" />}
              {state.robot_enabled ? "Activo" : "Inactivo"}
            </Badge>
            {state.emergency_stop && <Badge variant="destructive"><ShieldAlert size={12} className="mr-1" /> Emergency Stop</Badge>}
            <Badge variant="outline">Real trading: {state.live_trading_available ? "Disponible" : "Bloqueado"}</Badge>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {[
          { k: "backtest", label: "Backtesting" },
          { k: "paper", label: "Paper Trading" },
          { k: "config", label: "Configuración" },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setSubtab(s.k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${subtab === s.k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`robot-subtab-${s.k}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {subtab === "backtest" && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5 flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Símbolo</label>
              <Select value={btSymbol} onValueChange={setBtSymbol}>
                <SelectTrigger className="w-32" data-testid="bt-symbol"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...(config.symbols_active || []), ...(config.symbols_prepared || [])].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Timeframe</label>
              <Select value={btInterval} onValueChange={setBtInterval}>
                <SelectTrigger className="w-24" data-testid="bt-interval"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROBOT_TF_OPTIONS.map((tf) => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Días</label>
              <Select value={String(btDays)} onValueChange={(v) => setBtDays(Number(v))}>
                <SelectTrigger className="w-24" data-testid="bt-days"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROBOT_DAYS_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d}d</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={runBacktest} disabled={running} className="ml-auto" data-testid="bt-run">
              <Play size={14} className="mr-2" />
              {running ? "Ejecutando…" : "Ejecutar backtest"}
            </Button>
          </div>

          {backtest && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard testid="bt-kpi-roi" label="ROI" value={fmtPct(backtest.metrics.roi_pct)} hint={`P&L ${fmtEUR(backtest.metrics.pnl_eur)}`} trend={backtest.metrics.roi_pct} />
                <KpiCard testid="bt-kpi-winrate" label="Win Rate" value={`${backtest.metrics.win_rate_pct}%`} hint={`${backtest.metrics.wins}W / ${backtest.metrics.losses}L`} />
                <KpiCard testid="bt-kpi-drawdown" label="Max Drawdown" value={`-${backtest.metrics.max_drawdown_pct}%`} hint={`Sharpe ${backtest.metrics.sharpe}`} trend={-1} />
                <KpiCard testid="bt-kpi-pf" label="Profit Factor" value={backtest.metrics.profit_factor === null ? "∞" : backtest.metrics.profit_factor} hint={`${backtest.metrics.trades_count} trades`} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard testid="bt-kpi-avgwin" label="Ganancia media" value={fmtEUR(backtest.metrics.avg_win_eur)} />
                <KpiCard testid="bt-kpi-avgloss" label="Pérdida media" value={fmtEUR(backtest.metrics.avg_loss_eur)} />
                <KpiCard testid="bt-kpi-fees" label="Comisiones simuladas" value={fmtEUR(backtest.metrics.fees_total_eur)} />
                <KpiCard testid="bt-kpi-equity" label="Equity final" value={fmtEUR(backtest.metrics.final_equity)} />
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <h4 className="font-semibold mb-3 flex items-center gap-2"><LineChartIcon size={14} /> Curva Equity</h4>
                <div style={{ width: "100%", height: 260, minWidth: 300, minHeight: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={backtest.equity_curve.map((e) => ({ t: new Date(e.time_ms).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }), equity: e.equity }))}>
                      <XAxis dataKey="t" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} domain={["dataMin", "dataMax"]} tickFormatter={(v) => fmtEUR(v)} width={90} />
                      <Tooltip formatter={(v) => fmtEUR(v)} />
                      <Line type="monotone" dataKey="equity" stroke="#3B82F6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {backtest.trades.length > 0 && (
                <div className="rounded-2xl border border-border bg-card">
                  <div className="p-5 pb-3 font-semibold">Operaciones ({backtest.trades.length})</div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-muted-foreground tracking-widest sticky top-0 bg-card">
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-4">Fecha</th>
                          <th className="text-left py-2 px-4">Lado</th>
                          <th className="text-right py-2 px-4">Precio</th>
                          <th className="text-right py-2 px-4">Total</th>
                          <th className="text-right py-2 px-4">P&L</th>
                          <th className="text-left py-2 px-4">Motivo</th>
                        </tr>
                      </thead>
                      <tbody className="tabular-nums">
                        {backtest.trades.map((t, i) => (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="py-2 px-4 text-xs">{new Date(t.time_ms).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                            <td className={`py-2 px-4 font-semibold ${t.side === "BUY" ? "text-emerald-600" : "text-red-600"}`}>{t.side}</td>
                            <td className="py-2 px-4 text-right">{fmtNum(t.price, 4)}</td>
                            <td className="py-2 px-4 text-right">{fmtEUR(t.quote_qty)}</td>
                            <td className={`py-2 px-4 text-right font-semibold ${t.pnl_eur > 0 ? "text-emerald-600" : t.pnl_eur < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                              {t.pnl_eur !== undefined ? `${fmtEUR(t.pnl_eur)} (${t.pnl_pct > 0 ? "+" : ""}${t.pnl_pct?.toFixed(2)}%)` : "—"}
                            </td>
                            <td className="py-2 px-4 text-xs text-muted-foreground">{t.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {subtab === "paper" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testid="pt-kpi-equity" label="Equity total" value={fmtEUR(state.total_equity_eur)} hint={`Inicial ${fmtEUR(state.initial_capital_eur)}`} />
            <KpiCard testid="pt-kpi-cash" label="Efectivo" value={fmtEUR(state.cash_eur)} hint={`Invertido ${fmtEUR(state.invested_eur)}`} />
            <KpiCard testid="pt-kpi-roi" label="ROI acumulado" value={fmtPct(state.roi_pct)} hint={`Realizado ${fmtEUR(state.realized_pnl_eur)}`} trend={state.roi_pct} />
            <KpiCard testid="pt-kpi-trades" label="Operaciones" value={state.trades_count} hint={`${state.positions.length} abiertas`} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={runPaperStep} disabled={running || state.emergency_stop || !state.robot_enabled} data-testid="pt-step-btn">
              <Play size={14} className="mr-2" /> Ejecutar iteración manual
            </Button>
            <Button variant="outline" onClick={resetPaper} data-testid="pt-reset-btn">
              <RotateCcw size={14} className="mr-2" /> Reset portfolio virtual
            </Button>
            <Button variant="outline" onClick={loadAll}>
              <RefreshCw size={14} className="mr-2" /> Refrescar
            </Button>
            <div className="text-xs text-muted-foreground ml-auto">Bucle automático cada 15 min si Robot activado y sin Emergency Stop.</div>
          </div>
          <div className="rounded-2xl border border-border bg-card">
            <div className="p-5 pb-3 font-semibold">Posiciones abiertas ({state.positions.length})</div>
            {state.positions.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Ninguna posición abierta.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground tracking-widest">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-4">Símbolo</th>
                      <th className="text-right py-2 px-4">Cantidad</th>
                      <th className="text-right py-2 px-4">Entrada</th>
                      <th className="text-right py-2 px-4">Actual</th>
                      <th className="text-right py-2 px-4">SL</th>
                      <th className="text-right py-2 px-4">TP</th>
                      <th className="text-right py-2 px-4">Valor</th>
                      <th className="text-right py-2 px-4">P&L</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {state.positions.map((p) => (
                      <tr key={p.symbol} className="border-b border-border last:border-0" data-testid={`pt-pos-${p.symbol}`}>
                        <td className="py-2 px-4 font-semibold">{p.symbol}</td>
                        <td className="py-2 px-4 text-right">{fmtNum(p.qty, 8)}</td>
                        <td className="py-2 px-4 text-right">{fmtNum(p.entry_price, 4)}</td>
                        <td className="py-2 px-4 text-right">{fmtNum(p.current_price, 4)}</td>
                        <td className="py-2 px-4 text-right text-red-600">{fmtNum(p.stop_loss_price, 4)}</td>
                        <td className="py-2 px-4 text-right text-emerald-600">{fmtNum(p.take_profit_price, 4)}</td>
                        <td className="py-2 px-4 text-right font-semibold">{fmtEUR(p.current_value_eur)}</td>
                        <td className={`py-2 px-4 text-right font-semibold ${p.pnl_eur > 0 ? "text-emerald-600" : p.pnl_eur < 0 ? "text-red-600" : ""}`}>
                          {fmtEUR(p.pnl_eur)} ({p.pnl_pct > 0 ? "+" : ""}{p.pnl_pct}%)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-card">
            <div className="p-5 pb-3 font-semibold">Últimas operaciones ({state.recent_trades.length})</div>
            {state.recent_trades.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Sin operaciones aún.</div>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground tracking-widest sticky top-0 bg-card">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-4">Fecha</th>
                      <th className="text-left py-2 px-4">Símbolo</th>
                      <th className="text-left py-2 px-4">Lado</th>
                      <th className="text-right py-2 px-4">Precio</th>
                      <th className="text-right py-2 px-4">Total</th>
                      <th className="text-right py-2 px-4">P&L</th>
                      <th className="text-left py-2 px-4">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {state.recent_trades.map((t, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="py-2 px-4 text-xs">{new Date(t.time_ms).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="py-2 px-4 font-semibold">{t.symbol}</td>
                        <td className={`py-2 px-4 font-semibold ${t.side === "BUY" ? "text-emerald-600" : "text-red-600"}`}>{t.side}</td>
                        <td className="py-2 px-4 text-right">{fmtNum(t.price, 4)}</td>
                        <td className="py-2 px-4 text-right">{fmtEUR(t.quote_qty)}</td>
                        <td className={`py-2 px-4 text-right font-semibold ${t.pnl_eur > 0 ? "text-emerald-600" : t.pnl_eur < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                          {t.pnl_eur !== undefined && t.pnl_eur !== null ? fmtEUR(t.pnl_eur) : "—"}
                        </td>
                        <td className="py-2 px-4 text-xs text-muted-foreground">{t.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {subtab === "config" && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h4 className="font-semibold mb-4 flex items-center gap-2"><Bot size={14} /> Parámetros de estrategia</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <CfgField label="Capital inicial (€)" hint="Portfolio virtual" value={config.initial_capital_eur} onChange={(v) => updateCfg("initial_capital_eur", parseFloat(v) || 0)} testid="cfg-initial" />
              <CfgField label="Capital por operación (%)" hint="% del cash por BUY" value={config.capital_per_trade_pct} onChange={(v) => updateCfg("capital_per_trade_pct", parseFloat(v) || 0)} testid="cfg-per-trade" />
              <CfgField label="Comisión simulada (%)" hint="por lado" value={config.fee_pct} onChange={(v) => updateCfg("fee_pct", parseFloat(v) || 0)} testid="cfg-fee" />
              <CfgField label="Score entrada ≥" hint="0-100" value={config.entry_score} onChange={(v) => updateCfg("entry_score", parseFloat(v) || 0)} testid="cfg-entry-score" />
              <CfgField label="RSI máx. entrada" hint="RSI < X para BUY" value={config.max_rsi_for_entry} onChange={(v) => updateCfg("max_rsi_for_entry", parseFloat(v) || 0)} testid="cfg-rsi-max" />
              <CfgField label="Score salida ≤" hint="negativo, para SELL" value={config.exit_score} onChange={(v) => updateCfg("exit_score", parseFloat(v) || 0)} testid="cfg-exit-score" />
              <CfgField label="Stop-Loss (%)" hint="pérdida máx." value={config.stop_loss_pct} onChange={(v) => updateCfg("stop_loss_pct", parseFloat(v) || 0)} testid="cfg-sl" />
              <CfgField label="Take-Profit (%)" hint="beneficio objetivo" value={config.take_profit_pct} onChange={(v) => updateCfg("take_profit_pct", parseFloat(v) || 0)} testid="cfg-tp" />
              <CfgField label="Cooldown (min)" hint="entre operaciones/símbolo" value={config.cooldown_minutes} onChange={(v) => updateCfg("cooldown_minutes", parseInt(v) || 0)} testid="cfg-cooldown" />
            </div>
            <div className="mt-4">
              <label className="text-xs text-muted-foreground block mb-1">Timeframe operativo</label>
              <Select value={config.timeframe} onValueChange={(v) => updateCfg("timeframe", v)}>
                <SelectTrigger className="w-32" data-testid="cfg-timeframe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROBOT_TF_OPTIONS.map((tf) => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-4">
              <label className="text-xs text-muted-foreground block mb-2">Símbolos activos</label>
              <div className="flex flex-wrap gap-2">
                {[...(config.symbols_active || []), ...(config.symbols_prepared || [])].map((s) => {
                  const active = (config.symbols_active || []).includes(s);
                  return (
                    <button
                      key={s}
                      onClick={() => {
                        const cur = new Set(config.symbols_active || []);
                        if (cur.has(s)) cur.delete(s); else cur.add(s);
                        updateCfg("symbols_active", Array.from(cur));
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}
                      data-testid={`cfg-sym-${s}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Button onClick={saveCfg} disabled={!dirty || saving} data-testid="cfg-save">
                <Save size={14} className="mr-2" /> {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
              {dirty && <span className="text-xs text-amber-600">Cambios sin guardar</span>}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Estrategia base V1.3.</b> El motor usa las señales de V1.2 con Fibonacci como factor principal (peso 2.0), seguido de patrones de velas (1.5), MACD (1.2), Estocástico (1.0) y RSI (0.8). Órdenes reales <b>bloqueadas</b> (stubs con NotImplementedError) hasta V1.4. Emergency Stop bloquea únicamente ejecución del robot; backtest, fiscalidad, patrimonio y sincronización siguen operativos.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CfgField({ label, hint, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <Input
        type="number"
        step="0.1"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 tabular-nums"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

